/**
 * Signalbox — the finance team's recurring work by period.
 *
 * What is due, who owns it, whether it is done, and who signed it off.
 *
 * Two kinds of work, kept apart on the tab bar. The TIMETABLE (monthly,
 * quarterly, annual) is the regular service that runs every period whatever else
 * happens, with due dates set as offsets from the period end. PROGRAMMES are
 * dated runs of work that come round on a cycle, like the budget round, with
 * milestones on real calendar dates. The names are the railway's own: a timetable
 * is the booked service, and anything outside it is a special working.
 *
 * MY TASKS comes first on the bar, because that is what most people open
 * Signalbox to see: everything outstanding assigned to you, timetable and
 * programmes together, soonest first.
 *
 * Every due date arrives resolved from the server (working days depend on the
 * gov.uk bank holiday list). This file derives STATUS from those dates, which has
 * to happen client-side so a row turns overdue while the page is open.
 *
 * The task list lives in EDITS & STATUS and the periods and programmes in
 * SIGNALBOX PREFERENCES - both admin-only, and both inside this tab rather than
 * in the app's Admin area, so everything about Signalbox is in one place. There is
 * no roster: people come from the app's own user list, and each one's role is
 * shown where work is assigned.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MeResponse } from '../api'
import { ApiError } from '../api'
import * as sb from '../signalbox/api'
import {
  CADENCES,
  type Board,
  type Cadence,
  type CycleOut,
  type MilestoneOut,
  type PeriodOut,
  type ProgrammeOut,
} from '../signalbox/api'
import {
  MONTHS,
  STATUS_LABEL,
  type BoardIndex,
  type MineRow,
  type Row,
  type Status,
  WINDOWS,
  day,
  daysBetween,
  fmtDate,
  fmtStamp,
  initialsOf,
  milestoneStatus,
  nameFor,
  outstandingFor,
  periodEndLabel,
  periodStatus,
  rowsFor,
  startOfToday,
} from '../signalbox/status'
import { exportOutstanding, printOutstanding } from '../signalbox/printExport'
import Preferences from './signalbox/Preferences'

const UNGROUPED = 'Not part of a run'

type View =
  | { kind: 'mine' }
  | { kind: 'period' }
  | { kind: 'programme'; id: number }
  | { kind: 'prefs' }

export default function Signalbox({ me }: { me: MeResponse }) {
  const today = useMemo(() => startOfToday(), [])
  const [board, setBoard] = useState<Board | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [view, setView] = useState<View>({ kind: 'mine' })
  const [cadence, setCadence] = useState<Cadence>('monthly')
  const [fy, setFy] = useState<number | null>(null)
  const [periodId, setPeriodId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [mineWindow, setMineWindow] = useState('14')

  const load = useCallback(async () => {
    try {
      setBoard(await sb.getBoard())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /* Every write refetches. The board is small, and a refetch means the screen
     always shows what the server actually holds rather than an optimistic guess
     that might have been refused. */
  const act = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
      return true
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <section className="panel"><p className="muted">Loading Signalbox…</p></section>

  if (!board) {
    return (
      <section className="panel">
        <h2>Signalbox</h2>
        <div className="alert alert--error">{error ?? 'Signalbox could not be loaded.'}</div>
      </section>
    )
  }

  const index: BoardIndex = {
    byId: new Map(board.definitions.map((d) => [d.id, d])),
    attachmentsByTask: board.attachments.reduce((m, a) => {
      if (a.taskId != null) m.set(a.taskId, (m.get(a.taskId) ?? 0) + 1)
      return m
    }, new Map<number, number>()),
  }

  const mine = outstandingFor({
    upn: board.me.upn,
    periods: board.periods,
    occurrences: board.occurrences,
    index,
    programmes: board.programmes,
    today,
    window: mineWindow,
  })

  /* Only programmes with an open cycle appear — one that has not started yet
     would be an empty tab inviting a click. A finished one-off drops off too. */
  const liveProgrammes = board.programmes.filter(
    (p) => !p.finished && p.cycles.some((c) => c.open)
  )

  const tabs = (
    <SignalboxTabs
      view={view}
      setView={(v) => {
        setView(v)
        setExpanded(null)
      }}
      cadence={cadence}
      setCadence={(c) => {
        setCadence(c)
        setPeriodId(null)
        setExpanded(null)
      }}
      mineCount={mine.length}
      programmes={liveProgrammes}
      isAdmin={me.isAdmin}
    />
  )

  const shared = { board, today, index, tabs, busy, act, error, expanded, setExpanded }

  if (view.kind === 'mine') {
    return (
      <MyTasks
        {...shared}
        rows={mine}
        window={mineWindow}
        setWindow={setMineWindow}
        isAdmin={me.isAdmin}
      />
    )
  }

  /* Admin-only, and unreachable rather than merely hidden: a reader who somehow
     lands on it falls back to My Tasks. */
  if (view.kind === 'prefs') {
    if (!me.isAdmin) {
      return (
        <MyTasks
          {...shared}
          rows={mine}
          window={mineWindow}
          setWindow={setMineWindow}
          isAdmin={false}
        />
      )
    }
    return (
      <section className="panel">
        <header className="panel__head">
          <div>
            <h2>SignalBox Preferences</h2>
            <p className="panel__sub">The periods the board runs on, and the programmes.</p>
          </div>
        </header>
        {error && <div className="alert alert--error">{error}</div>}
        {tabs}
        <Preferences board={board} busy={busy} act={act} today={today} />
      </section>
    )
  }

  if (view.kind === 'programme') {
    const prog = board.programmes.find((p) => p.id === view.id)
    if (!prog) return <section className="panel">{tabs}<p className="muted">That programme is gone.</p></section>
    return <ProgrammeBoard {...shared} programme={prog} isAdmin={me.isAdmin} />
  }

  return (
    <PeriodBoard
      {...shared}
      cadence={cadence}
      fy={fy}
      setFy={(y) => {
        setFy(y)
        setPeriodId(null)
      }}
      periodId={periodId}
      setPeriodId={setPeriodId}
      isAdmin={me.isAdmin}
    />
  )
}

// ─── The cadence bar ─────────────────────────────────────────────────────────

/**
 * Order and grouping matter. YOUR work first, because that is what most people
 * open Signalbox to see. Then the TIMETABLE — the regular service that runs every
 * period whatever happens. Then PROGRAMMES, dated runs of work that come round on
 * a cycle. The labels mean nobody has to learn which is which.
 */
function SignalboxTabs({
  view,
  setView,
  cadence,
  setCadence,
  mineCount,
  programmes,
  isAdmin,
}: {
  view: View
  setView: (v: View) => void
  cadence: Cadence
  setCadence: (c: Cadence) => void
  mineCount: number
  programmes: ProgrammeOut[]
  isAdmin: boolean
}) {
  const onMine = view.kind === 'mine'

  return (
    <div className="sub-tab-bar">
      <span className="tm-tabgrp">
        <span className="tm-tabgroup"> </span>
        <span className="tm-tabrow">
          <button
            type="button"
            className={'sub-tab sub-tab--mine' + (onMine ? ' active' : '')}
            title="Everything outstanding assigned to you, timetable and programmes together"
            onClick={() => setView({ kind: 'mine' })}
          >
            My Tasks
            {mineCount > 0 && (
              <span className={'tm-minecount' + (onMine ? ' on' : '')}>{mineCount}</span>
            )}
          </button>
        </span>
      </span>

      <span className="tm-tabdiv" />

      <span className="tm-tabgrp">
        <span className="tm-tabgroup">Timetable</span>
        <span className="tm-tabrow">
          {CADENCES.map(([id, label]) => (
            <button
              type="button"
              key={id}
              className={'sub-tab' + (view.kind === 'period' && cadence === id ? ' active' : '')}
              onClick={() => {
                setView({ kind: 'period' })
                setCadence(id)
              }}
            >
              {label}
            </button>
          ))}
        </span>
      </span>

      {programmes.length > 0 && (
        <>
          <span className="tm-tabdiv" />
          <span className="tm-tabgrp">
            <span className="tm-tabgroup">Programmes</span>
            <span className="tm-tabrow">
              {programmes.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  title={p.blurb ?? p.name}
                  className={
                    'sub-tab sub-tab--prog' +
                    (view.kind === 'programme' && view.id === p.id ? ' active' : '')
                  }
                  onClick={() => setView({ kind: 'programme', id: p.id })}
                >
                  {p.name}
                </button>
              ))}
            </span>
          </span>
        </>
      )}

      {/* Admin, to the right of the programmes. Editing tasks happens ON the board
          itself — select a task and its panel opens on the right — so the only
          admin destination is Preferences: the periods and the programmes. A
          reader never sees it. */}
      {isAdmin && (
        <>
          <span className="tm-tabdiv" />
          <span className="tm-tabgrp">
            <span className="tm-tabgroup">Set up</span>
            <span className="tm-tabrow">
              <button
                type="button"
                className={'sub-tab sub-tab--admin' + (view.kind === 'prefs' ? ' active' : '')}
                title="The periods the board runs on, and the programmes"
                onClick={() => setView({ kind: 'prefs' })}
              >
                SignalBox Preferences
              </button>
            </span>
          </span>
        </>
      )}
    </div>
  )
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

interface SharedProps {
  board: Board
  today: Date
  index: BoardIndex
  tabs: JSX.Element
  busy: boolean
  /** Resolves true if the write went through, false if the server refused it. */
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  error: string | null
  expanded: string | null
  setExpanded: (k: string | null) => void
}

function Tile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat-tile">
      <div className="stat-tile__label">{label}</div>
      <div className="stat-tile__value">{value}</div>
    </div>
  )
}

function DaysCell({
  status,
  due,
  completedAt,
  today,
}: {
  status: Status
  due: Date
  completedAt: string | null
  today: Date
}) {
  if (status === 'future') return <span className="tm-days tm-days--none">—</span>

  if (status === 'complete' && completedAt) {
    const d = daysBetween(new Date(completedAt), due)
    const text =
      d > 0 ? `${d} day${d === 1 ? '' : 's'} early` : d === 0 ? 'on the day' : `${Math.abs(d)} day${d === -1 ? '' : 's'} late`
    return <span className={`tm-days ${d >= 0 ? 'tm-days--early' : 'tm-days--late'}`}>{text}</span>
  }

  const d = daysBetween(today, due)
  if (d < 0) {
    return (
      <span className="tm-days tm-days--late">
        {Math.abs(d)} day{d === -1 ? '' : 's'} overdue
      </span>
    )
  }
  if (d <= 3) {
    return (
      <span className="tm-days tm-days--soon">
        {d} day{d === 1 ? '' : 's'} to go
      </span>
    )
  }
  return <span className="tm-days">{d} days to go</span>
}

/** The bank holiday horizon, said out loud rather than quietly guessed past. */
function HolidayWarning({ board }: { board: Board }) {
  const through = board.bankHolidays.through
  if (!through) {
    return (
      <div className="alert alert--error tm-banner">
        The bank holiday list is empty, so working-day due dates cannot be trusted. Run the{' '}
        <strong>Signalbox bank holidays</strong> job on the Scheduler tab.
      </div>
    )
  }
  const horizon = day(through)
  const monthsLeft = (horizon.getFullYear() - board.today.slice(0, 4).length) * 0
  void monthsLeft
  const daysLeft = daysBetween(day(board.today), horizon)
  if (daysLeft > 120) return null
  return (
    <div className="alert tm-banner">
      The bank holiday list only reaches <strong>{fmtDate(horizon)}</strong>. Due dates past that
      date assume no further bank holidays until the list is refreshed.
    </div>
  )
}

// ─── The period board ────────────────────────────────────────────────────────

function PeriodBoard(
  props: SharedProps & {
    cadence: Cadence
    fy: number | null
    setFy: (y: number) => void
    periodId: string | null
    setPeriodId: (id: string) => void
    isAdmin: boolean
  }
) {
  const { board, today, index, tabs, busy, act, error, expanded, setExpanded, cadence, isAdmin } = props
  const [show, setShow] = useState<'all' | 'mine'>('all')
  const [order, setOrder] = useState<'group' | 'date'>('group')
  const [draft, setDraft] = useState<Record<string, string>>({})

  /* On the Annual tab a financial year IS the period, so a year row and a period
     row would be the same control twice. List the years as the periods instead
     and drop the year row. */
  const isAnnual = cadence === 'annual'
  /* EVERY period in the season, not only the open ones. An unopened month still
     tells you something — that it is coming, and that nothing has started in it.
     Showing only the open three made the rest of the year look as though it did
     not exist. Occurrences are still generated for open periods only, so an
     unopened pill has nothing behind it and says so. */
  const allPeriods = board.periods[cadence]

  const fys = [...new Set(allPeriods.map((p) => fyOfPeriod(p)))].sort()
  const fy = props.fy ?? fys.find((y) => y === fyOfPeriod0(board, cadence)) ?? fys[0] ?? board.fys[0]
  const periods = isAnnual ? allPeriods : allPeriods.filter((p) => fyOfPeriod(p) === fy)

  const rowsOf = (p: PeriodOut) => rowsFor(board.occurrences[cadence], p, index, today)

  /* Land on the period that needs attention — the most recent ended one that is
     not fully complete. Today's period is normally empty, so opening on it would
     show nothing to do. */
  const period = useMemo(() => {
    const found = periods.find((p) => p.id === props.periodId)
    if (found) return found
    /* Default to the period that needs attention: the most recent ENDED and OPEN
       one that is not fully complete. Never default to an unopened period —
       there is nothing in it to look at. */
    const live = periods.filter((p) => p.open)
    const ended = live.filter((p) => day(p.end) < today)
    for (let i = ended.length - 1; i >= 0; i--) {
      const st = periodStatus(rowsOf(ended[i]).map((r) => r.status))
      if (st === 'open' || st === 'overdue') return ended[i]
    }
    /* Nothing has ended yet (early in the season, or the first month is still
       running): land on the period being worked NOW — the earliest open one that
       has not closed. Falling through to the LAST open period opened the board on
       a month two ahead of anything anybody was doing. */
    return (
      ended[ended.length - 1] ??
      live.find((p) => day(p.end) >= today) ??
      live[live.length - 1] ??
      periods[0] ??
      null
    )
  }, [periods, props.periodId, board])

  if (!period) {
    return (
      <section className="panel">
        <Head />
        {tabs}
        <div className="empty">
          <p>No {cadence} period is open yet.</p>
          <p className="muted">
            Periods open on a schedule. An admin can open one early from Admin → Signalbox →
            Periods.
          </p>
        </div>
      </section>
    )
  }

  const all = rowsOf(period)
  const rows = show === 'mine' ? all.filter((r) => r.assigneeUpn === board.me.upn) : all
  const done = rows.filter((r) => r.status === 'complete').length
  const openCount = rows.filter((r) => r.status === 'open').length
  const overdue = rows.filter((r) => r.status === 'overdue').length

  /* CLOSED — every task in an ended period is complete. Drives "Reopen period". */
  const closed = all.length > 0 && day(period.end) < today && all.every((r) => r.status === 'complete')

  const groups = groupRows(rows, order)

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Signalbox</h2>
          <p className="panel__sub">
            Recurring finance tasks by period — who owns each one, when it is due, and whether it is
            done.
          </p>
        </div>
        {closed && isAdmin && (
          <button
            className="btn btn--small"
            disabled={busy}
            onClick={() =>
              confirm(
                `Reopen ${period.long}? Every completion stamp in the period is cleared and the tasks go back to outstanding.`
              ) && act(() => sb.reopenPeriod(period.id))
            }
          >
            ↺ Reopen period
          </button>
        )}
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      <HolidayWarning board={board} />
      {tabs}

      <div className="pill-stack">
        {!isAnnual && fys.length > 1 && (
          <div className="pill-row">
            <span className="pill-row__label">Financial year</span>
            {[...fys].reverse().map((y) => (
              <button
                key={y}
                className={'month-pill' + (fy === y ? ' active' : '')}
                onClick={() => props.setFy(y)}
              >
                FY{String(y % 100).padStart(2, '0')}-{String((y + 1) % 100).padStart(2, '0')}
              </button>
            ))}
          </div>
        )}

        <div className="pill-row">
          <span className="pill-row__label">{isAnnual ? 'Financial year' : 'Period'}</span>
          {periods.map((p) => (
            <button
              key={p.id}
              className={
                'month-pill' +
                (p.id === period.id ? ' active' : '') +
                (p.open ? '' : ' month-pill--unopened')
              }
              title={
                p.open
                  ? `${p.long} — ends ${fmtDate(day(p.end))}`
                  : `${p.long} — not open yet, opens ${fmtDate(day(p.openFrom))}`
              }
              onClick={() => {
                props.setPeriodId(p.id)
                setExpanded(null)
              }}
            >
              <i
                className={
                  p.open
                    ? `status-dot status-dot--${periodStatus(rowsOf(p).map((r) => r.status))}`
                    : 'status-dot status-dot--unopened'
                }
              />
              {p.label}
            </button>
          ))}
        </div>

        <div className="pill-legend">
          <span><i className="status-dot status-dot--future" /> Not started</span>
          <span><i className="status-dot status-dot--open" /> In progress</span>
          <span><i className="status-dot status-dot--overdue" /> Overdue</span>
          <span><i className="status-dot status-dot--complete" /> All complete</span>
          <span><i className="status-dot status-dot--unopened" /> Not open yet</span>
        </div>
        <p className="tm-scope">
          A period stays Not started until it ends, then runs In progress while the work is done. It
          only turns <strong>Overdue</strong> once everything still outstanding is past its due date
          — a single late task in a month that is otherwise moving does not colour the whole period.
        </p>
      </div>

      {!period.open && (
        <div className="tm-period-head">
          <div>
            <h3>{period.long}</h3>
            <p className="muted">
              Not open yet — it opens by itself on {fmtDate(day(period.openFrom))}, and no tasks are
              generated until it does.
            </p>
          </div>
          {isAdmin && (
            <button
              className="btn btn--small"
              disabled={busy}
              onClick={() => act(() => sb.openPeriod(period.id))}
            >
              Open now
            </button>
          )}
        </div>
      )}

      {period.open && (
      <>
      <div className="tm-period-head">
        <div>
          <h3>{period.long}</h3>
          <p className="muted">
            Period ended {fmtDate(day(period.end))}
            {day(period.end) >= today
              ? ' — still running. Tasks can be signed off early.'
              : closed
                ? ' — closed.'
                : ''}
            {period.openedEarly && ' · opened early'}
          </p>
        </div>
      </div>

      <div className="tm-filterbar tm-filterbar--board">
        <span className="tm-filter-label">Show</span>
        <button className={'month-pill' + (show === 'all' ? ' active' : '')} onClick={() => setShow('all')}>
          Everyone <span className="muted">{all.length}</span>
        </button>
        <button className={'month-pill' + (show === 'mine' ? ' active' : '')} onClick={() => setShow('mine')}>
          Just mine <span className="muted">{all.filter((r) => r.assigneeUpn === board.me.upn).length}</span>
        </button>
        <span className="tm-spacer" />
        <span className="tm-filter-label">Order</span>
        <button className={'month-pill' + (order === 'group' ? ' active' : '')} onClick={() => setOrder('group')}>
          By run
        </button>
        <button className={'month-pill' + (order === 'date' ? ' active' : '')} onClick={() => setOrder('date')}>
          By date
        </button>
      </div>

      <div className="stat-tiles tm-tiles">
        <Tile label="Tasks" value={rows.length} />
        <Tile label="Complete" value={done} />
        <Tile label="Open" value={openCount} />
        <Tile label="Overdue" value={overdue} />
        <Tile
          label="Progress"
          value={rows.length ? `${Math.round((done / rows.length) * 100)}%` : '—'}
        />
      </div>

      <div className="table-wrap tm-stickyhead">
        <table className="table tm-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Assigned to</th>
              <th>Due</th>
              <th>Days</th>
              <th>Status</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No tasks in this period.
                </td>
              </tr>
            )}
            {groups.map(([groupName, groupRows]) => (
              <GroupBlock
                key={groupName}
                name={groupName}
                rows={groupRows}
                showHeader={order === 'group' && groups.length > 1}
                board={board}
                today={today}
                period={period}
                expanded={expanded}
                setExpanded={setExpanded}
                busy={busy}
                act={act}
                draft={draft}
                setDraft={setDraft}
                isAdmin={isAdmin}
              />
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </section>
  )
}

/** The financial year of the period the board would land on by default. */
const fyOfPeriod0 = (board: Board, cadence: Cadence): number => {
  const live = board.periods[cadence].filter((p) => p.open)
  const last = live[live.length - 1]
  return last ? fyOfPeriod(last) : board.fys[0]
}

const Head = () => (
  <header className="panel__head">
    <div>
      <h2>Signalbox</h2>
      <p className="panel__sub">
        Recurring finance tasks by period — who owns each one, when it is due, and whether it is
        done.
      </p>
    </div>
  </header>
)

const fyOfPeriod = (p: PeriodOut): number => {
  const end = day(p.end)
  return end.getMonth() >= 2 ? end.getFullYear() : end.getFullYear() - 1
}

function groupRows(rows: Row[], order: 'group' | 'date'): Array<[string, Row[]]> {
  if (order === 'date') return [['', rows]]
  const map = new Map<string, Row[]>()
  for (const r of rows) {
    const key = r.group ?? UNGROUPED
    map.set(key, [...(map.get(key) ?? []), r])
  }
  // Named runs first, in name order; the ungrouped remainder last.
  return [...map.entries()].sort(([a], [b]) =>
    a === UNGROUPED ? 1 : b === UNGROUPED ? -1 : a.localeCompare(b)
  )
}

function GroupBlock({
  name,
  rows,
  showHeader,
  board,
  today,
  period,
  expanded,
  setExpanded,
  busy,
  act,
  draft,
  setDraft,
  isAdmin,
}: {
  name: string
  rows: Row[]
  showHeader: boolean
  board: Board
  today: Date
  period: PeriodOut
  expanded: string | null
  setExpanded: (k: string | null) => void
  busy: boolean
  /** Resolves true if the write went through, false if the server refused it. */
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  draft: Record<string, string>
  setDraft: (d: Record<string, string>) => void
  isAdmin: boolean
}) {
  const done = rows.filter((r) => r.status === 'complete').length
  const overdue = rows.filter((r) => r.status === 'overdue').length

  return (
    <>
      {showHeader && (
        <tr className="tm-grouphead">
          <td colSpan={7}>
            <span className="strong">{name}</span>
            <span className="tm-groupcount">
              {done} of {rows.length}
            </span>
            {overdue > 0 && <span className="tm-groupover">{overdue} overdue</span>}
          </td>
        </tr>
      )}
      {rows.map((r) => (
        <TaskRow
          key={r.key}
          row={r}
          board={board}
          today={today}
          period={period}
          expanded={expanded === r.key}
          onToggle={() => setExpanded(expanded === r.key ? null : r.key)}
          busy={busy}
          act={act}
          draft={draft[r.key] ?? ''}
          setDraft={(v) => setDraft({ ...draft, [r.key]: v })}
          isAdmin={isAdmin}
        />
      ))}
    </>
  )
}

function TaskRow({
  row,
  board,
  today,
  period,
  expanded,
  onToggle,
  busy,
  act,
  draft,
  setDraft,
  isAdmin,
}: {
  row: Row
  board: Board
  today: Date
  period: PeriodOut
  expanded: boolean
  onToggle: () => void
  busy: boolean
  /** Resolves true if the write went through, false if the server refused it. */
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  draft: string
  setDraft: (v: string) => void
  isAdmin: boolean
}) {
  const ref = { periodId: row.periodId, taskId: row.taskId }
  /* A reader signs off their OWN work — that is the point of the thing. An admin
     can sign off anybody's, which is how cover during leave works. */
  const mayComplete = isAdmin || row.assigneeUpn === board.me.upn
  const files = board.attachments.filter((a) => a.taskId === row.taskId)

  return (
    <>
      <tr data-status={row.status}>
        <td className="tm-task-name">
          <span className="strong">{row.name}</span>
          {row.ref && <span className="tm-task-ref">{row.ref}</span>}
          {(row.instructions || row.hasFiles) && (
            <span className="tm-hasdoc" title="Has instructions">
              📄
            </span>
          )}
        </td>
        <td>
          <Assignee board={board} upn={row.assigneeUpn} />
        </td>
        <td className="mono">{fmtDate(row.due)}</td>
        <td>
          <DaysCell status={row.status} due={row.due} completedAt={row.completedAt} today={today} />
        </td>
        <td>
          <span className={`status-tag status-tag--${row.status}`}>{STATUS_LABEL[row.status]}</span>
        </td>
        <td>
          <button
            className="tm-noteflag"
            data-has={row.notes.length > 0}
            onClick={onToggle}
            title={row.notes.length ? `${row.notes.length} note(s) on this period` : 'Add a note'}
          >
            {row.notes.length ? '🗒' : '＋'}
          </button>
        </td>
        <td className="row-actions">
          {row.status === 'complete' ? (
            isAdmin ? (
              <button className="btn btn--small" disabled={busy} onClick={() => act(() => sb.reopen(ref))}>
                ↺ Undo
              </button>
            ) : (
              <span className="muted" title="Only an admin can undo a sign-off">
                signed off
              </span>
            )
          ) : mayComplete ? (
            <button
              className="btn btn--small btn--primary"
              disabled={busy}
              onClick={() => act(() => sb.complete(ref))}
            >
              Mark complete
            </button>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="tm-detail-row">
          <td colSpan={7} className="tm-detail">
            <div className="tm-detail__cols">
              <section>
                {(row.instructions || files.length > 0) && (
                  <div className="tm-standing">
                    <h5>From task setup</h5>
                    {row.instructions ? <p>{row.instructions}</p> : <p className="muted">No standing notes.</p>}
                    {files.map((f) => (
                      <a key={f.id} className="tm-instr" href={`/api/signalbox/attachments/${f.id}`}>
                        📎 {f.fileName} <span className="muted">{humanSize(f.sizeBytes)}</span>
                      </a>
                    ))}
                  </div>
                )}

                <h4>Notes — {period.long} only</h4>
                {row.notes.length ? (
                  <ul className="tm-notelist">
                    {row.notes.map((n, i) => (
                      <li key={i}>
                        {n.text}
                        <span className="tm-note-meta">
                          {nameFor(board.people, n.by)} · {fmtStamp(n.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="tm-empty-note">No notes on this period yet.</p>
                )}

                <textarea
                  className="tm-notebox"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Add a note about this period's task…"
                />
                <div className="tm-note-actions">
                  <button
                    className="btn btn--small btn--primary"
                    disabled={busy || !draft.trim()}
                    onClick={() =>
                      act(async () => {
                        await sb.addNote({ ...ref, text: draft.trim() })
                        setDraft('')
                      })
                    }
                  >
                    Add note
                  </button>
                  <span className="muted">Stamped with your name and the time.</span>
                </div>
                <p className="tm-scope">
                  Notes belong to this period's occurrence of the task — they do not carry forward to
                  the next period, and they are not a note on the task itself.
                </p>
              </section>

              <section>
                <h4>Audit trail</h4>
                <dl className="tm-facts">
                  <dt>Task</dt>
                  <dd>{row.name}</dd>
                  <dt>Period</dt>
                  <dd>{period.long}</dd>
                  <dt>Owner</dt>
                  <dd>{nameFor(board.people, row.assigneeUpn)}</dd>
                  <dt>Due</dt>
                  <dd>{fmtDate(row.due)}</dd>
                  <dt>Status</dt>
                  <dd>{STATUS_LABEL[row.status]}</dd>
                  {row.completedAt ? (
                    <>
                      <dt>Completed</dt>
                      <dd className="tm-stamp">{fmtStamp(row.completedAt)}</dd>
                      <dt>By</dt>
                      <dd>{nameFor(board.people, row.completedBy)}</dd>
                    </>
                  ) : (
                    <>
                      <dt>Completed</dt>
                      <dd className="muted">—</dd>
                    </>
                  )}
                </dl>
              </section>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * Who owns the work, and what they can do in this app. The role is shown here
 * because Signalbox has no roster of its own — it assigns to the app's users, and
 * "reader" versus "admin" is the difference between somebody who ticks their own
 * work off and somebody who can also change the task list.
 */
function Assignee({ board, upn }: { board: Board; upn: string | null }) {
  if (!upn) return <span className="muted">Unassigned</span>
  const person = board.people.find((p) => p.upn === upn)
  const name = person?.displayName ?? upn
  return (
    <span className="tm-assignee">
      <span className="tm-initials" title={upn}>
        {initialsOf(name)}
      </span>
      {name}
      {person && <span className="role-pill" data-role={person.role}>{person.role}</span>}
      {!person && (
        <span className="role-pill" data-role="none" title="No access to the finance app">
          no access
        </span>
      )}
    </span>
  )
}

const humanSize = (n: number): string =>
  n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`

// ─── My Tasks ────────────────────────────────────────────────────────────────

/**
 * One list of everything outstanding for the signed-in user, from all three
 * cadences and every programme, in due-date order. The cadence and the period it
 * belongs to are on every row, because without them "Restricted Funds" could be
 * July's or the year's.
 *
 * "Assigned to" is deliberately absent — every row is yours.
 */
function MyTasks(
  props: SharedProps & {
    rows: MineRow[]
    window: string
    setWindow: (w: string) => void
    isAdmin: boolean
  }
) {
  const { board, today, tabs, busy, act, error, rows, window: win, setWindow } = props
  const overdue = rows.filter((r) => r.status === 'overdue').length
  const next = rows.find((r) => r.status !== 'overdue')
  const myName = board.me.displayName ?? board.me.upn

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>My Tasks</h2>
          <p className="panel__sub">
            Everything outstanding assigned to {myName} — the timetable and every programme
            together, soonest first.
          </p>
        </div>
        {rows.length > 0 && (
          <div className="tm-headactions">
            <button
              className="btn btn--small"
              title="A printable list with a tick column"
              onClick={() => printOutstanding({ name: myName, rows, today })}
            >
              🖨 Print
            </button>
            <button
              className="btn btn--small"
              title="Download as CSV, which Excel opens directly"
              onClick={() => exportOutstanding({ name: myName, rows, today })}
            >
              ↯ Excel
            </button>
          </div>
        )}
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      <HolidayWarning board={board} />
      {tabs}

      <div className="tm-filterbar tm-filterbar--board">
        <span className="tm-filter-label">Show tasks due in</span>
        {WINDOWS.map(([v, l]) => (
          <button
            key={v}
            className={'month-pill' + (win === v ? ' active' : '')}
            onClick={() => setWindow(v)}
          >
            {l}
          </button>
        ))}
        {overdue > 0 && (
          <span className="muted tm-ceased-note">Overdue work always shows, however far back</span>
        )}
      </div>

      {!rows.length ? (
        <div className="empty">
          <p>Nothing due in this window.</p>
          <p className="muted">
            Every task assigned to you in a period that has ended is complete. Work for periods
            still running appears here once it falls due.
          </p>
        </div>
      ) : (
        <>
          <div className="stat-tiles tm-tiles">
            <Tile label="Outstanding" value={rows.length} />
            <Tile label="Overdue" value={overdue} />
            <Tile label="To do" value={rows.length - overdue} />
            <Tile label="Next due" value={next ? fmtDate(next.due) : '—'} />
          </div>

          <div className="table-wrap tm-stickyhead">
            <table className="table tm-table tm-mine-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Period</th>
                  <th>Due</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} data-status={r.status}>
                    <td className="tm-task-name">
                      <span className="strong">{r.name}</span>
                      {r.ref && <span className="tm-task-ref">{r.ref}</span>}
                      {r.hasFiles && <span className="tm-hasdoc" title="Has instructions">📄</span>}
                    </td>
                    <td className="tm-periodcell">
                      <span className="tm-cadtag" data-c={r.cadence}>
                        {r.cadenceLabel}
                      </span>
                      <span className="tm-periodwhen">{r.periodLabel}</span>
                    </td>
                    <td className="mono">{fmtDate(r.due)}</td>
                    <td>
                      <DaysCell status={r.status} due={r.due} completedAt={null} today={today} />
                    </td>
                    <td>
                      <span className={`status-tag status-tag--${r.status}`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="row-actions">
                      <button
                        className="btn btn--small btn--primary"
                        disabled={busy}
                        onClick={() =>
                          act(() =>
                            sb.complete({
                              periodId: r.periodId,
                              ...(r.taskId ? { taskId: r.taskId } : {}),
                              ...(r.programmeTaskId ? { programmeTaskId: r.programmeTaskId } : {}),
                            })
                          )
                        }
                      >
                        Mark complete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}

// ─── Programme board ─────────────────────────────────────────────────────────

/**
 * A programme is a dated run of work on a cycle. Its milestones carry real
 * calendar dates rather than offsets, and a cycle carries its OWN dates: the
 * anchors hold year on year, but the middle dates move when the board meetings
 * move — and last year's must not be rewritten when this year's change.
 */
function ProgrammeBoard(props: SharedProps & { programme: ProgrammeOut; isAdmin: boolean }) {
  const { board, today, tabs, busy, act, error, expanded, setExpanded, programme, isAdmin } = props
  const openCycles = programme.cycles.filter((c) => c.open)
  const [cycleFy, setCycleFy] = useState<number | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})

  const cycle: CycleOut | null =
    openCycles.find((c) => c.fy === cycleFy) ?? openCycles[openCycles.length - 1] ?? null

  if (!cycle) {
    return (
      <section className="panel">
        {tabs}
        <div className="empty">
          <p>No cycle of {programme.name} is open yet.</p>
        </div>
      </section>
    )
  }

  const statuses = cycle.milestones.map((m) => milestoneStatus(m, today))
  const done = statuses.filter((s) => s === 'complete').length
  const overdue = statuses.filter((s) => s === 'overdue').length
  const next = cycle.milestones.find((m) => !m.completedAt)

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>{programme.name}</h2>
          <p className="panel__sub">
            {programme.blurb ?? 'A dated run of work on a cycle.'}
            {programme.repeatKind === 'annual' && programme.naming === 'next' && (
              <> This cycle runs in {cycle.runsIn} and sets the {cycle.label} budget.</>
            )}
          </p>
        </div>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {tabs}

      {programme.repeatKind === 'annual' && openCycles.length > 1 && (
        <div className="pill-stack">
          <div className="pill-row">
            <span className="pill-row__label">Cycle</span>
            {openCycles.map((c) => (
              <button
                key={c.fy}
                className={'month-pill' + (c.fy === cycle.fy ? ' active' : '')}
                onClick={() => {
                  setCycleFy(c.fy)
                  setExpanded(null)
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="tm-period-head">
        <div>
          <h3>
            {programme.repeatKind === 'oneoff' ? programme.name : `${cycle.label} cycle`}
            {cycle.openedEarly && <span className="tm-task-ref">opened early</span>}
          </h3>
          <p className="muted">
            Started {fmtDate(day(cycle.start))}
            {cycle.overrides > 0 && (
              <>
                {' · '}
                <strong>
                  {cycle.overrides} date{cycle.overrides === 1 ? '' : 's'} moved from the plan
                </strong>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="stat-tiles tm-tiles">
        <Tile label="Milestones" value={cycle.milestones.length} />
        <Tile label="Complete" value={done} />
        <Tile label="Overdue" value={overdue} />
        <Tile label="Next due" value={next ? fmtDate(day(next.due)) : '—'} />
        <Tile
          label="Progress"
          value={
            cycle.milestones.length
              ? `${Math.round((done / cycle.milestones.length) * 100)}%`
              : '—'
          }
        />
      </div>

      <div className="table-wrap tm-stickyhead">
        <table className="table tm-table">
          <thead>
            <tr>
              <th>Milestone</th>
              <th>Assigned to</th>
              <th>Due</th>
              <th>Days</th>
              <th>Status</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {cycle.milestones.map((m) => (
              <MilestoneRow
                key={m.programmeTaskId}
                m={m}
                cycle={cycle}
                board={board}
                today={today}
                busy={busy}
                act={act}
                isAdmin={isAdmin}
                expanded={expanded === `${cycle.periodId}|${m.programmeTaskId}`}
                onToggle={() =>
                  setExpanded(
                    expanded === `${cycle.periodId}|${m.programmeTaskId}`
                      ? null
                      : `${cycle.periodId}|${m.programmeTaskId}`
                  )
                }
                draft={draft[`${cycle.periodId}|${m.programmeTaskId}`] ?? ''}
                setDraft={(v) =>
                  setDraft({ ...draft, [`${cycle.periodId}|${m.programmeTaskId}`]: v })
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function MilestoneRow({
  m,
  cycle,
  board,
  today,
  busy,
  act,
  isAdmin,
  expanded,
  onToggle,
  draft,
  setDraft,
}: {
  m: MilestoneOut
  cycle: CycleOut
  board: Board
  today: Date
  busy: boolean
  /** Resolves true if the write went through, false if the server refused it. */
  act: (fn: () => Promise<unknown>) => Promise<boolean>
  isAdmin: boolean
  expanded: boolean
  onToggle: () => void
  draft: string
  setDraft: (v: string) => void
}) {
  const status = milestoneStatus(m, today)
  const due = day(m.due)
  const ref = { periodId: cycle.periodId, programmeTaskId: m.programmeTaskId }
  const mayComplete = isAdmin || m.assigneeUpn === board.me.upn

  return (
    <>
      <tr data-status={status}>
        <td className="tm-task-name">
          <span className="strong">{m.name}</span>
          {m.ref && <span className="tm-task-ref">{m.ref}</span>}
          {/* An override is visible AS one. A moved date that looks like a planned
              date invites somebody to "correct" it back. */}
          {m.overridden && (
            <span className="tm-task-ref" title={`Planned for ${fmtDate(day(m.planned))}`}>
              moved from {fmtDate(day(m.planned))}
            </span>
          )}
        </td>
        <td>
          <Assignee board={board} upn={m.assigneeUpn} />
        </td>
        <td className="mono">{fmtDate(due)}</td>
        <td>
          <DaysCell status={status} due={due} completedAt={m.completedAt} today={today} />
        </td>
        <td>
          <span className={`status-tag status-tag--${status}`}>{STATUS_LABEL[status]}</span>
        </td>
        <td>
          <button className="tm-noteflag" data-has={m.notes.length > 0} onClick={onToggle}>
            {m.notes.length ? '🗒' : '＋'}
          </button>
        </td>
        <td className="row-actions">
          {status === 'complete' ? (
            isAdmin ? (
              <button className="btn btn--small" disabled={busy} onClick={() => act(() => sb.reopen(ref))}>
                ↺ Undo
              </button>
            ) : (
              <span className="muted">signed off</span>
            )
          ) : mayComplete ? (
            <button
              className="btn btn--small btn--primary"
              disabled={busy}
              onClick={() => act(() => sb.complete(ref))}
            >
              Mark complete
            </button>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
      </tr>

      {expanded && (
        <tr className="tm-detail-row">
          <td colSpan={7} className="tm-detail">
            <div className="tm-detail__cols">
              <section>
                {m.instructions && (
                  <div className="tm-standing">
                    <h5>From the milestone</h5>
                    <p>{m.instructions}</p>
                  </div>
                )}
                <h4>Notes — {cycle.label} cycle only</h4>
                {m.notes.length ? (
                  <ul className="tm-notelist">
                    {m.notes.map((n, i) => (
                      <li key={i}>
                        {n.text}
                        <span className="tm-note-meta">
                          {nameFor(board.people, n.by)} · {fmtStamp(n.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="tm-empty-note">No notes on this cycle yet.</p>
                )}
                <textarea
                  className="tm-notebox"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Add a note about this cycle's milestone…"
                />
                <div className="tm-note-actions">
                  <button
                    className="btn btn--small btn--primary"
                    disabled={busy || !draft.trim()}
                    onClick={() =>
                      act(async () => {
                        await sb.addNote({ ...ref, text: draft.trim() })
                        setDraft('')
                      })
                    }
                  >
                    Add note
                  </button>
                </div>
              </section>
              <section>
                <h4>Audit trail</h4>
                <dl className="tm-facts">
                  <dt>Milestone</dt>
                  <dd>{m.name}</dd>
                  <dt>Cycle</dt>
                  <dd>
                    {cycle.label} <span className="muted">runs in {cycle.runsIn}</span>
                  </dd>
                  <dt>Planned</dt>
                  <dd>{fmtDate(day(m.planned))}</dd>
                  <dt>Due</dt>
                  <dd>{fmtDate(due)}</dd>
                  <dt>Status</dt>
                  <dd>{STATUS_LABEL[status]}</dd>
                  {m.completedAt ? (
                    <>
                      <dt>Completed</dt>
                      <dd className="tm-stamp">{fmtStamp(m.completedAt)}</dd>
                      <dt>By</dt>
                      <dd>{nameFor(board.people, m.completedBy)}</dd>
                    </>
                  ) : (
                    <>
                      <dt>Completed</dt>
                      <dd className="muted">—</dd>
                    </>
                  )}
                </dl>
              </section>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export { MONTHS, periodEndLabel }
